import React, { createContext, useContext, useReducer, useEffect } from 'react';
import sessionService from '../services/sessionService.js';
import { SESSION_STATUS } from '../types/session.js';

// Action types
const SESSION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_SESSION: 'SET_SESSION',
  SET_ERROR: 'SET_ERROR',
  UPDATE_ANSWER: 'UPDATE_ANSWER',
  ADD_UPLOAD: 'ADD_UPLOAD',
  SET_REQUIREMENTS: 'SET_REQUIREMENTS',
  SET_ARTIFACTS: 'SET_ARTIFACTS',
  SET_PROJECT_BRIEF: 'SET_PROJECT_BRIEF',
  CLEAR_SESSION: 'CLEAR_SESSION'
};

// Initial state
const initialState = {
  session: null,
  loading: false,
  error: null,
  currentQuestion: null,
  progress: 0
};

// Reducer
const sessionReducer = (state, action) => {
  switch (action.type) {
    case SESSION_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload, error: null };
    
    case SESSION_ACTIONS.SET_SESSION:
      const session = action.payload;
      // Find first unanswered question
      const currentQuestion = session?.questions?.find(q => !q.answered) || null;
      const answeredCount = session?.questions?.filter(q => q.answered)?.length || 0;
      const totalQuestions = session?.questions?.length || 0;
      const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
      
      // Update session metadata with actual counts
      if (session) {
        session.metadata = {
          ...session.metadata,
          completedQuestions: answeredCount,
          totalQuestions: totalQuestions
        };
      }
      
      console.log('🔄 SET_SESSION:', {
        sessionId: session?.sessionId,
        questionsCount: totalQuestions,
        answeredCount,
        currentQuestion: currentQuestion?.id,
        firstQuestion: session?.questions?.[0]?.text
      });
      
      return { 
        ...state, 
        session: {
          ...session,
          currentQuestion
        }, 
        currentQuestion,
        progress,
        loading: false, 
        error: null 
      };
    
    case SESSION_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case SESSION_ACTIONS.UPDATE_ANSWER:
      if (!state.session) return state;
      
      const updatedSession = { ...state.session };
      updatedSession.answers[action.payload.questionId] = action.payload.answer;
      
      // Mark the question as answered
      const questionToUpdate = updatedSession.questions.find(q => q.id === action.payload.questionId);
      if (questionToUpdate) {
        questionToUpdate.answered = true;
        questionToUpdate.answer = action.payload.answer;
      }
      
      const newAnsweredCount = updatedSession.questions.filter(q => q.answered).length;
      const newTotalQuestions = updatedSession.questions.length;
      updatedSession.metadata.completedQuestions = newAnsweredCount;
      updatedSession.metadata.totalQuestions = newTotalQuestions;
      
      const nextQuestion = updatedSession.questions.find(q => !q.answered) || null;
      const newProgress = newTotalQuestions > 0 ? (newAnsweredCount / newTotalQuestions) * 100 : 0;
      
      return {
        ...state,
        session: updatedSession,
        currentQuestion: nextQuestion,
        progress: newProgress
      };
    
    case SESSION_ACTIONS.ADD_UPLOAD:
      if (!state.session) return state;
      
      return {
        ...state,
        session: {
          ...state.session,
          uploads: [...state.session.uploads, action.payload]
        }
      };
    
    case SESSION_ACTIONS.SET_REQUIREMENTS:
      if (!state.session) return state;
      
      return {
        ...state,
        session: {
          ...state.session,
          requirements: action.payload,
          status: SESSION_STATUS.REVIEWING
        }
      };
    
    case SESSION_ACTIONS.SET_ARTIFACTS:
      if (!state.session) return state;
      
      return {
        ...state,
        session: {
          ...state.session,
          artifacts: action.payload,
          status: SESSION_STATUS.COMPLETE
        }
      };
    
    case SESSION_ACTIONS.SET_PROJECT_BRIEF:
      if (!state.session) return state;
      
      return {
        ...state,
        session: {
          ...state.session,
          projectBrief: action.payload
        }
      };
    
    case SESSION_ACTIONS.CLEAR_SESSION:
      return initialState;
    
    default:
      return state;
  }
};

// Context
const SessionContext = createContext();

// Provider component
export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('diagramloom_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        dispatch({ type: SESSION_ACTIONS.SET_SESSION, payload: session });
      } catch (error) {
        console.error('Failed to load saved session:', error);
        localStorage.removeItem('diagramloom_session');
      }
    }
  }, []);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (state.session) {
      localStorage.setItem('diagramloom_session', JSON.stringify(state.session));
    }
  }, [state.session]);

  // Actions
  const createSession = async (userId, projectName, initialIdea) => {
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const session = await sessionService.createSession(userId, projectName, initialIdea);
      dispatch({ type: SESSION_ACTIONS.SET_SESSION, payload: session });
      return session;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const loadSession = async (sessionId) => {
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const session = await sessionService.getSession(sessionId);
      if (session) {
        dispatch({ type: SESSION_ACTIONS.SET_SESSION, payload: session });
      } else {
        dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: 'Session not found' });
      }
      return session;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const submitAnswer = async (questionId, value, metadata = {}) => {
    if (!state.session) throw new Error('No active session');
    
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const updatedSession = await sessionService.submitAnswer(
        state.session.sessionId, 
        questionId, 
        value, 
        metadata
      );
      
      // Update the answer in local state
      dispatch({ 
        type: SESSION_ACTIONS.UPDATE_ANSWER, 
        payload: { questionId, answer: { value, metadata } }
      });
      
      // Then update the full session
      dispatch({ type: SESSION_ACTIONS.SET_SESSION, payload: updatedSession });
      return updatedSession;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const uploadFile = async (file, type = 'document') => {
    if (!state.session) throw new Error('No active session');
    
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const upload = await sessionService.uploadFile(state.session.sessionId, file, type);
      dispatch({ type: SESSION_ACTIONS.ADD_UPLOAD, payload: upload });
      return upload;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const generateRequirements = async () => {
    if (!state.session) throw new Error('No active session');
    
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const requirements = await sessionService.generateRequirements(state.session.sessionId);
      dispatch({ type: SESSION_ACTIONS.SET_REQUIREMENTS, payload: requirements });
      return requirements;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const generateArtifacts = async () => {
    if (!state.session) throw new Error('No active session');
    
    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });
    try {
      const artifacts = await sessionService.generateArtifacts(state.session.sessionId);
      dispatch({ type: SESSION_ACTIONS.SET_ARTIFACTS, payload: artifacts });
      return artifacts;
    } catch (error) {
      dispatch({ type: SESSION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const generateProjectBrief = () => {
    if (!state.session) throw new Error('No active session');
    
    const questions = state.session.questions || [];
    const answers = state.session.answers || {};
    
    const getAnswer = (questionId) => {
      const answerFromAnswers = answers[questionId];
      if (answerFromAnswers) {
        if (answerFromAnswers.value !== undefined) {
          const value = answerFromAnswers.value;
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          return value || 'Not specified';
        }
        return answerFromAnswers;
      }
      
      const question = questions.find(q => q.id === questionId);
      const answer = question?.answer;
      
      if (answer && typeof answer === 'object' && answer.value !== undefined) {
        if (Array.isArray(answer.value)) {
          return answer.value.join(', ');
        }
        return answer.value || 'Not specified';
      }
      
      if (Array.isArray(answer)) {
        return answer.join(', ');
      }
      return answer || 'Not specified';
    };

    const brief = {
      projectName: state.session.projectName || 'ProphetPlay.ai',
      description: state.session.initialIdea || 'A high-volume, real-time prediction platform for sports, entertainment, and financial events without real money wagering.',
      businessModel: getAnswer('business-model'),
      scaleTier: getAnswer('scale-tier'),
      availability: getAnswer('availability-slo'),
      compliance: getAnswer('compliance-requirements'),
      dataTypes: getAnswer('data-sensitivity'),
      authentication: getAnswer('authentication-model'),
      dataModel: getAnswer('data-model'),
      integrations: getAnswer('external-integrations'),
      deployment: getAnswer('deployment-strategy'),
      maturity: getAnswer('operational-maturity')
    };
    
    dispatch({ type: SESSION_ACTIONS.SET_PROJECT_BRIEF, payload: brief });
    return brief;
  };

  const updateSessionData = (updates) => {
    if (!state.session) return;
    
    const updatedSession = {
      ...state.session,
      ...updates
    };
    
    dispatch({ type: SESSION_ACTIONS.SET_SESSION, payload: updatedSession });
  };

  const clearSession = () => {
    localStorage.removeItem('diagramloom_session');
    dispatch({ type: SESSION_ACTIONS.CLEAR_SESSION });
  };

  const value = {
    ...state,
    sessionData: state.session, // Add sessionData alias for easier access
    createSession,
    loadSession,
    submitAnswer,
    uploadFile,
    generateRequirements,
    generateArtifacts,
    generateProjectBrief,
    updateSessionData,
    clearSession
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

// Hook to use session context
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;